import { Menu } from 'antd';
import { EditOutlined, SearchOutlined, LogoutOutlined, UserSwitchOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { TextService } from '../../Services/TextService';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { useState, useEffect } from 'react';
import { PublicSiteService } from '../../Services/PublicSiteService';
import { WebApiPublicSiteDtoEditableCustomListDto } from '../../Repository/eventbookyapi';

export const PublicSiteMenu = () => {
    const GetText = TextService.GetText;
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const [hasPublicView, setHasPublicView] = useState(false);
    const [editableCustomLists, setEditableCustomLists] = useState([] as WebApiPublicSiteDtoEditableCustomListDto[]);
    const [familyMemberSelfServiceLimit, setFamilyMemberSelfServiceLimit] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkPublicView = async () => {
            if (!params.companyurl) {
                setLoading(false);
                return;
            }

            try {
                const [result, lists, session] = await Promise.all([
                    PublicSiteService.HasPublicMemberView(params.companyurl),
                    PublicSiteService.GetEditableCustomLists(),
                    PublicSiteService.GetSession()
                ]);
                setHasPublicView(result);
                setEditableCustomLists(lists || []);
                setFamilyMemberSelfServiceLimit(session.familyMemberSelfServiceLimit ?? 0);
            } catch (error) {
                console.error('Error checking public view:', error);
                setHasPublicView(false);
                setEditableCustomLists([]);
                setFamilyMemberSelfServiceLimit(0);
            } finally {
                setLoading(false);
            }
        };
        checkPublicView();
    }, [params.companyurl]);

    if (loading) {
        return null;
    }

    const lang = getCurrentLanguage();
    const companyUrl = params.companyurl;

    const editMemberPath = `/${lang}/${companyUrl}/editmember`;
    const memberSearchPath = `/${lang}/${companyUrl}/membersearch`;
    const familyMembersPath = `/${lang}/${companyUrl}/familymembers`;

    // Determine selected key based on current path
    let selectedKey = 'edit';
    if (location.pathname.includes('membersearch')) {
        selectedKey = 'search';
    } else if (location.pathname.includes('familymembers')) {
        selectedKey = 'familymembers';
    } else if (location.pathname.includes('editmember')) {
        selectedKey = 'edit';
    } else if (location.pathname.includes('public-customlist/')) {
        const customListId = location.pathname.split('/public-customlist/')[1]?.split('/')[0];
        selectedKey = customListId ? `customlist-${customListId}` : selectedKey;
    }

    const navItems = [
        {
            key: 'edit',
            icon: <EditOutlined />,
            label: GetText('PublicSiteMenu_EditProfile'),
            onClick: () => navigate(editMemberPath)
        }
    ];

    if (familyMemberSelfServiceLimit > 0) {
        navItems.push({
            key: 'familymembers',
            icon: <TeamOutlined />,
            label: GetText('PublicSiteFamilyMembers_Title'),
            onClick: () => navigate(familyMembersPath)
        });
    }

    editableCustomLists.forEach((list) => {
        if (!list.customListId) {
            return;
        }

        navItems.push({
            key: `customlist-${list.customListId}`,
            icon: <UserSwitchOutlined />,
            label: list.name || GetText('CustomList_List'),
            onClick: () => navigate(`/${lang}/${companyUrl}/public-customlist/${list.customListId}`)
        });
    });

    // Show Search Members to the right of custom list items
    if (hasPublicView) {
        navItems.push({
            key: 'search',
            icon: <SearchOutlined />,
            label: GetText('PublicSiteMenu_SearchMembers'),
            onClick: () => navigate(memberSearchPath)
        });
    }

    const logoutItem = [
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: GetText('PublicSiteMenu_Logout'),
            onClick: async () => {
                try {
                    await PublicSiteService.Logout();
                } catch {
                } finally {
                    navigate(editMemberPath);
                    window.location.reload();
                }
            }
        }
    ];

    return (
        <div style={{ maxWidth: '960px', margin: '0 auto', padding: '0 20px', marginBottom: '24px' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '2px solid #f0f0f0'
            }}>
                <Menu
                    mode="horizontal"
                    selectedKeys={[selectedKey]}
                    items={navItems}
                    style={{
                        border: 'none',
                        flex: 1
                    }}
                    overflowedIndicator={null}
                />
                <Menu
                    mode="horizontal"
                    items={logoutItem}
                    style={{
                        border: 'none'
                    }}
                    overflowedIndicator={null}
                />
            </div>
        </div>
    );
};
